import {
  mapBlockSets,
  type ExerciseNode,
  type Expr,
  type GroupItem,
  type Line,
  type Prop,
  type TemplateNode,
  type WorkoutItem,
  type WorkoutNode,
} from './ast'

/**
 * The small edits a GUI makes to the tree. They are here rather than in an editor because they
 * are the same edits on every platform, and because a tree operation is worth testing on its
 * own — an editor writes canonical text, so what these produce is the file somebody keeps.
 */

const NOWHERE = { line: 0, column: 0 }

/** Where a set line sits: which workout, which item of it, which exercise, which line. */
export interface LineRef {
  readonly workout: number
  readonly item: number
  readonly within: number
  readonly line: number
}

export function mapWorkout(
  node: TemplateNode,
  index: number,
  change: (workout: WorkoutNode) => WorkoutNode,
): TemplateNode {
  return {
    ...node,
    workouts: node.workouts.map((workout, position) =>
      position === index ? change(workout) : workout,
    ),
  }
}

export function mapGroup(
  workout: WorkoutNode,
  index: number,
  change: (group: GroupItem) => GroupItem,
): WorkoutNode {
  return {
    ...workout,
    items: workout.items.map((held, position) =>
      position === index && held.type === 'group' ? change(held) : held,
    ),
  }
}

export function mapExercise(
  workout: WorkoutNode,
  item: number,
  within: number,
  change: (exercise: ExerciseNode) => ExerciseNode,
): WorkoutNode {
  return {
    ...workout,
    items: workout.items.map((held, index) => {
      if (index !== item) {
        return held
      }
      if (held.type === 'exercise') {
        return { ...held, exercise: change(held.exercise) }
      }
      return {
        ...held,
        exercises: held.exercises.map((exercise, position) =>
          position === within ? change(exercise) : exercise,
        ),
      }
    }),
  }
}

export function removeExercise(workout: WorkoutNode, item: number, within: number): WorkoutNode {
  const held = workout.items[item]
  if (!held) {
    return workout
  }
  if (held.type === 'exercise') {
    return { ...workout, items: workout.items.filter((_, index) => index !== item) }
  }
  const kept = held.exercises.filter((_, index) => index !== within)
  if (kept.length === 0) {
    return { ...workout, items: workout.items.filter((_, index) => index !== item) }
  }
  return mapGroup(workout, item, (group) => ({ ...group, exercises: kept }))
}

/** Rest cannot live inside a superset, so joining one sheds whatever rests were carried. */
export function strippedOfRest(exercise: ExerciseNode): ExerciseNode {
  return {
    ...exercise,
    props: exercise.props.filter((prop) => prop.name !== 'rest'),
    lines: exercise.lines.map((line): Line => {
      if (line.type === 'dynamic') {
        return mapBlockSets(line, (set) => ({ ...set, mods: { ...set.mods, rest: undefined } }))
      }
      return { ...line, mods: { ...line.mods, rest: undefined } }
    }),
  }
}

/** Pairs an exercise with the one after it. The first rest found becomes the group's. */
export function groupWithNext(workout: WorkoutNode, index: number): WorkoutNode {
  const first = workout.items[index]
  const second = workout.items[index + 1]
  if (first?.type !== 'exercise' || second?.type !== 'exercise') {
    return workout
  }
  const rest =
    first.exercise.props.find((prop) => prop.name === 'rest')?.value ??
    second.exercise.props.find((prop) => prop.name === 'rest')?.value ??
    firstLineRest(first.exercise)
  const group: GroupItem = {
    type: 'group',
    kind: 'superset',
    exercises: [strippedOfRest(first.exercise), strippedOfRest(second.exercise)],
    props: rest ? [{ name: 'rest', value: rest, at: NOWHERE }] : [],
    comments: [],
    at: NOWHERE,
  }
  return {
    ...workout,
    items: [...workout.items.slice(0, index), group, ...workout.items.slice(index + 2)],
  }
}

function firstLineRest(exercise: ExerciseNode): Expr | undefined {
  for (const line of exercise.lines) {
    if (line.type === 'set' && line.mods.rest) {
      return line.mods.rest
    }
  }
  return undefined
}

/** Standing alone again, each member takes the group's rest as its own. */
export function ungroup(workout: WorkoutNode, index: number): WorkoutNode {
  const group = workout.items[index]
  if (group?.type !== 'group') {
    return workout
  }
  const rest = group.props.find((prop) => prop.name === 'rest')?.value
  const members = group.exercises.map((member): WorkoutItem => {
    if (!rest) {
      return { type: 'exercise', exercise: member }
    }
    const props: Prop[] = [
      ...member.props.filter((prop) => prop.name !== 'rest'),
      { name: 'rest', value: rest, at: NOWHERE },
    ]
    return { type: 'exercise', exercise: { ...member, props } }
  })
  return {
    ...workout,
    items: [...workout.items.slice(0, index), ...members, ...workout.items.slice(index + 1)],
  }
}

/** One more exercise in a superset, sharing its rounds. */
export function addMember(workout: WorkoutNode, index: number): WorkoutNode {
  return mapGroup(workout, index, (group) => ({
    ...group,
    exercises: [...group.exercises, blankExercise()],
  }))
}

/** Wraps an exercise in a choose beside a fresh alternative, or grows the choose it heads. */
export function addAlternative(workout: WorkoutNode, index: number): WorkoutNode {
  const item = workout.items[index]
  if (item?.type === 'exercise') {
    const group: GroupItem = {
      type: 'group',
      kind: 'choose',
      exercises: [item.exercise, blankExercise()],
      props: [],
      comments: [],
      at: NOWHERE,
    }
    return {
      ...workout,
      items: workout.items.map((held, position) => (position === index ? group : held)),
    }
  }
  if (item?.type === 'group' && item.kind === 'choose') {
    return addMember(workout, index)
  }
  return workout
}

/** A list with what sits at `from` lifted out and put back at `to`. Off either end, it stays. */
function shifted<T>(items: readonly T[], from: number, to: number): readonly T[] {
  const held = items[from]
  if (held === undefined || from === to || to < 0 || to >= items.length) {
    return items
  }
  const without = items.filter((_, index) => index !== from)
  return [...without.slice(0, to), held, ...without.slice(to)]
}

export function moveWorkout(node: TemplateNode, from: number, to: number): TemplateNode {
  return { ...node, workouts: shifted(node.workouts, from, to) }
}

export function moveItem(workout: WorkoutNode, from: number, to: number): WorkoutNode {
  return { ...workout, items: shifted(workout.items, from, to) }
}

export function moveMember(
  workout: WorkoutNode,
  item: number,
  from: number,
  to: number,
): WorkoutNode {
  return mapGroup(workout, item, (group) => ({
    ...group,
    exercises: shifted(group.exercises, from, to),
  }))
}

export function moveLine(
  workout: WorkoutNode,
  item: number,
  within: number,
  from: number,
  to: number,
): WorkoutNode {
  return mapExercise(workout, item, within, (exercise) => ({
    ...exercise,
    lines: shifted(exercise.lines, from, to),
  }))
}

/** Where an exercise sits: which workout, which item of it, and which of that item's exercises. */
export interface ExerciseRef {
  readonly workout: number
  readonly item: number
  readonly within: number
}

/**
 * An exercise taken from where it is and put into a workout as an item of its own. Leaving a
 * superset it takes the group's rest with it, the way ungrouping the whole superset would.
 */
export function moveExercise(
  node: TemplateNode,
  from: ExerciseRef,
  toWorkout: number,
  toItem: number,
): TemplateNode {
  const source = node.workouts[from.workout]
  const item = source?.items[from.item]
  if (!source || !item || node.workouts[toWorkout] === undefined) {
    return node
  }
  const held = item.type === 'exercise' ? item.exercise : item.exercises[from.within]
  if (!held) {
    return node
  }
  const rest =
    item.type === 'group' && item.kind === 'superset'
      ? item.props.find((prop) => prop.name === 'rest')?.value
      : undefined
  const moved = rest ? withProp(held, 'rest', rest) : held

  // Taken out first, so the place it lands is read off the list it is actually landing in.
  const trimmed = node.workouts.map((workout, index) =>
    index === from.workout ? removeExercise(source, from.item, from.within) : workout,
  )
  const into = trimmed[toWorkout]
  if (!into) {
    return node
  }
  const at = Math.max(0, Math.min(toItem, into.items.length))
  const placed: WorkoutNode = {
    ...into,
    items: [
      ...into.items.slice(0, at),
      { type: 'exercise', exercise: moved },
      ...into.items.slice(at),
    ],
  }
  return {
    ...node,
    workouts: trimmed.map((workout, index) => (index === toWorkout ? placed : workout)),
  }
}

export function blankExercise(name = 'New exercise'): ExerciseNode {
  return { name, props: [], lines: [], comments: [], at: NOWHERE }
}

export function blankWorkout(name: string): WorkoutNode {
  return { name, props: [], items: [], comments: [], at: NOWHERE }
}

export function inSuperset(node: TemplateNode, ref: LineRef): boolean {
  const item = node.workouts[ref.workout]?.items[ref.item]
  return item?.type === 'group' && item.kind === 'superset'
}

export function exerciseAt(node: TemplateNode, ref: LineRef): ExerciseNode | undefined {
  const item = node.workouts[ref.workout]?.items[ref.item]
  if (!item) {
    return undefined
  }
  return item.type === 'exercise' ? item.exercise : item.exercises[ref.within]
}

/** A property set to a value, replacing whatever it held, or removed when nothing is given. */
export function withProp<T extends { readonly props: readonly Prop[] }>(
  node: T,
  name: string,
  value: Expr | null,
): T {
  const without = node.props.filter((prop) => prop.name !== name)
  return {
    ...node,
    props: value === null ? without : [...without, { name, value, at: NOWHERE }],
  }
}
